import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
  page: string;
  setPage: (page: string) => void;
}

export default function Layout({ children, page, setPage }: LayoutProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleNavClick = (targetPage: string) => {
    setPage(targetPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col font-sans overflow-x-hidden relative">
      <header className="sticky top-0 z-50 bg-gray-950/80 backdrop-blur-md border-b border-gray-900 px-4 md:px-6 py-4 flex items-center justify-between">
        {/* Logo and Brand Section */}
        <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => handleNavClick('home')}>
          <img 
            src="/logo.svg" 
            alt="GetQRPost Logo" 
            className="h-8 md:h-9 w-auto rounded-xl shadow-lg shadow-indigo-500/10"
          />
          <span className="text-xl font-bold bg-gradient-to-r from-gray-100 via-gray-200 to-gray-400 bg-clip-text text-transparent">
            GetQRPost
          </span>
        </div>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-2">
          <button 
            onClick={() => handleNavClick('home')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              page === 'home' || page === 'qr' 
                ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20' 
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/60'
            }`}
          >
            Home
          </button>
          <button 
            onClick={() => handleNavClick('blogs')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              page === 'blogs' 
                ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20' 
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/60'
            }`}
          >
            Blog
          </button>
          <button 
            onClick={() => handleNavClick('contact')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              page === 'contact' 
                ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20' 
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/60'
            }`}
          >
            Contact Us
          </button>
        </nav>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="md:hidden p-2 rounded-xl text-gray-400 hover:text-gray-200 hover:bg-gray-900/60 focus:outline-none transition-all"
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </header>

      {/* Mobile Dropdown Menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed top-[69px] left-0 right-0 bg-gray-950/95 backdrop-blur-lg border-b border-gray-900 z-40 p-4 flex flex-col gap-2 shadow-2xl animate-in fade-in slide-in-from-top-4 duration-200">
          <button
            onClick={() => {
              handleNavClick('home');
              setIsMenuOpen(false);
            }}
            className={`w-full text-left px-4 py-3.5 rounded-xl text-sm font-semibold transition-all ${
              page === 'home' || page === 'qr'
                ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/60'
            }`}
          >
            Home
          </button>
          <button
            onClick={() => {
              handleNavClick('blogs');
              setIsMenuOpen(false);
            }}
            className={`w-full text-left px-4 py-3.5 rounded-xl text-sm font-semibold transition-all ${
              page === 'blogs'
                ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/60'
            }`}
          >
            Blog
          </button>
          <button
            onClick={() => {
              handleNavClick('contact');
              setIsMenuOpen(false);
            }}
            className={`w-full text-left px-4 py-3.5 rounded-xl text-sm font-semibold transition-all ${
              page === 'contact'
                ? 'bg-indigo-600/10 text-indigo-400 border border-indigo-500/20'
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-900/60'
            }`}
          >
            Contact Us
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 md:px-6 py-10 flex flex-col">
        {children}
      </main>

      {/* Footer Section */}
      <footer className="bg-gray-950 border-t border-gray-900 px-4 md:px-6 py-8 mt-12 text-center text-xs text-gray-500">
        <div className="flex flex-col gap-6 max-w-6xl mx-auto">
          {/* SEO Cross-Links Section */}
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-gray-400 font-semibold border-b border-gray-900/40 pb-4">
            <span onClick={() => handleNavClick('link-qr')} className="hover:text-indigo-400 cursor-pointer transition-colors">Free Link QR Maker</span>
            <span onClick={() => handleNavClick('menu-qr')} className="hover:text-indigo-400 cursor-pointer transition-colors">Restaurant Menu QR</span>
            <span onClick={() => handleNavClick('wifi-qr')} className="hover:text-indigo-400 cursor-pointer transition-colors">Free WiFi QR Designer</span>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p>&copy; {new Date().getFullYear()} GetQRPost. 100% Free & Privacy-First QR Code Generator.</p>
            <div className="flex gap-4 text-gray-400">
              <span onClick={() => handleNavClick('privacy')} className="hover:text-gray-200 cursor-pointer transition-colors">Privacy Policy</span>
              <span onClick={() => handleNavClick('terms')} className="hover:text-gray-200 cursor-pointer transition-colors">Terms of Service</span>
              <span onClick={() => handleNavClick('about')} className="hover:text-gray-200 cursor-pointer transition-colors">About Us</span>
              <span onClick={() => handleNavClick('contact')} className="hover:text-gray-200 cursor-pointer transition-colors">Contact Us</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
